import React, { useEffect, useState, useMemo } from 'react';
import {
  Settings, Plus, Trash, RefreshCw, Loader2, ChevronDown, Users, CheckCircle2, Clock, Activity,
} from 'lucide-react';
import { ShieldCheck } from '@phosphor-icons/react';

import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { projectsService } from '../../services/projects';
import MetricCard from '../../components/projects/MetricCard';

import {
  ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar, Cell,
} from 'recharts';

import { useProject } from './ProjectContext';
import { useProjectAnalysis } from './useProjectAnalysis';

const AdosaPage: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { language } = useLanguage();

  const { project, adosaOverride, setAdosaOverride } = useProject();
  const { adosa, apcas, safeNum } = useProjectAnalysis();

  const cardBg = 'bg-[var(--surface)] border-[var(--border)]';
  const textMain = 'text-[var(--text-primary)]';
  const textSub = 'text-[var(--text-secondary)]';
  const inputBg = 'bg-[var(--surface-raised)] border-[var(--border)] text-[var(--text-primary)]';
  const labelStyle = `text-xs font-bold block mb-1 uppercase tracking-wide ${'text-[var(--text-secondary)]'}`;

  // ADOSA inline parameter state (accessible from the ADOSA tab)
  const [adosaParamsOpen, setAdosaParamsOpen] = useState(false);
  const [adosaParams, setAdosaParams] = useState<Record<string, any>>({});

  // Initialize adosaParams from project when project loads
  useEffect(() => {
    if (project) {
      const sme = project.sme_parameters || ({} as any);
      setAdosaParams({
        // ═══ DSR TAB: Towers & Positions ═══
        rest_factor_pct: sme.rest_factor_pct ?? 0,
        towers: sme.towers || [
          {
            name: 'Apron 1',
            positions: [
              { name: 'CWP 1', schedule_hours: 16, num_shifts: 2, is_operational: true },
              { name: 'CWP 2', schedule_hours: 0, num_shifts: 0, is_operational: true },
              { name: 'SUP', schedule_hours: 0, num_shifts: 0, is_operational: false },
            ],
          },
        ],
        // ═══ STAFFING TOOL ═══
        shift_cycles: sme.shift_cycles || [
          {
            name: 'Turno 1', work_days: 5, off_days: 3,
            shift_schedule: [
              { name: 'M', hours: 8, staff: 1 },
              { name: 'T', hours: 8, staff: 1 },
              { name: 'N', hours: 0, staff: 0 },
              { name: 'G', hours: 24, staff: 1 },
            ],
          },
          {
            name: 'Turno 2', work_days: 6, off_days: 4,
            shift_schedule: [
              { name: 'M', hours: 8, staff: 1 },
              { name: 'T', hours: 8, staff: 1 },
              { name: 'N', hours: 0, staff: 0 },
              { name: 'G', hours: 24, staff: 1 },
            ],
          },
        ],
        holidays_vacation_days: sme.holidays_vacation_days ?? 21,
        bank_holidays_days: sme.bank_holidays_days ?? 0,
        work_permits_days: sme.work_permits_days ?? 0,
        absenteeism_pct: sme.absenteeism_pct ?? 5,
        yearly_training_hours: sme.yearly_training_hours ?? 60,
        training_shift_duration: sme.training_shift_duration ?? 8,
        is_calendar_days: sme.is_calendar_days || false,
        safety_buffer_pct: sme.safety_buffer_pct ?? 0,
      });
    }
  }, [project]);

  const handleAdosaParamChange = (key: string, value: any) => {
    setAdosaParams(prev => ({ ...prev, [key]: value }));
  };

  // ADOSA local override — results from client-side recalculation
  const [adosaRecalculating, setAdosaRecalculating] = useState(false);

  // The staffing formulas used to run here in the browser and overwrite the
  // pipeline's KPIs without a trail. They now run on the server (staffing_engine,
  // faithful to the ADOS template) which also persists the roster inputs and
  // returns provenance; this just sends the form and shows the audited result.
  const recalculateStaffing = async () => {
    setAdosaRecalculating(true);
    try {
      const result = await projectsService.recalculateAdosa(project.id, {
        towers: adosaParams.towers,
        shift_cycles: adosaParams.shift_cycles,
        rest_factor_pct: adosaParams.rest_factor_pct,
        holidays_vacation_days: adosaParams.holidays_vacation_days,
        bank_holidays_days: adosaParams.bank_holidays_days,
        work_permits_days: adosaParams.work_permits_days,
        is_calendar_days: adosaParams.is_calendar_days,
        yearly_training_hours: adosaParams.yearly_training_hours,
        training_shift_duration: adosaParams.training_shift_duration,
        absenteeism_pct: adosaParams.absenteeism_pct,
        safety_buffer_pct: adosaParams.safety_buffer_pct,
      });
      setAdosaOverride(result);
    } catch (err) {
      console.error('ADOSA recalculation error:', err);
    } finally {
      setAdosaRecalculating(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // CLIENT-SIDE ROSTER GENERATOR — must be before early returns (Rules of Hooks)
  // ═══════════════════════════════════════════════════════════════
  const generateClientRoster = useMemo(() => {
    // Prefer adosaOverride (client-side recalculation) over backend data
    const res = project?.analysis_results || {};
    const p2 = (res as any)?.parts?.part2_adosa || (res as any)?.parts?.part2_ados || {};

    // DSR: prefer override
    const dsrRaw = adosaOverride?.dsr_real || p2.dsr_real || res.dsr_real || 0;
    const dsr = Math.max(1, Math.round(dsrRaw || 1));

    // Headcount: prefer override optimal/risk-free
    const rfStaff = adosaOverride?.headcount_for_roster || adosaOverride?.optimal_staffing
      || p2.headcount_for_roster || p2.risk_free_staffing || res.risk_free_staffing
      || p2.optimal_staffing || res.optimal_staffing || dsr + 2;
    const headcount = Math.max(dsr, Math.round(rfStaff));

    // Work/off cycle: prefer first shift_cycle from adosaParams
    const firstCycle = (adosaParams.shift_cycles || [])[0];
    const workDays = (adosaOverride && firstCycle) ? (parseFloat(firstCycle.work_days) || 6) : (p2.roster_work_days || 6);
    const offDays = (adosaOverride && firstCycle) ? (parseFloat(firstCycle.off_days) || 4) : (p2.roster_off_days || 4);
    const cycleLen = (workDays + offDays) || 10;

    // Vacation/training from adosaParams when override active
    const vacPerPerson = adosaOverride ? (parseFloat(adosaParams.holidays_vacation_days) || 21) : 21;
    const trainPerPerson = adosaOverride
      ? Math.ceil((parseFloat(adosaParams.yearly_training_hours) || 60) / (parseFloat(adosaParams.training_shift_duration) || 8))
      : Math.ceil(p2.training_days || 7);

    const monthDays = 30;
    const yearDays = 365;
    const staggerStep = cycleLen / headcount;

    // Monthly roster (T/L)
    const monthlyGrid: string[][] = [];
    for (let emp = 0; emp < headcount; emp++) {
      const offset = Math.round(emp * staggerStep) % cycleLen;
      const row: string[] = [];
      for (let d = 0; d < monthDays; d++) {
        row.push(((d + offset) % cycleLen) < workDays ? 'T' : 'L');
      }
      monthlyGrid.push(row);
    }
    const monthlyCoverage = Array.from({ length: monthDays }, (_, d) =>
      monthlyGrid.reduce((s, row) => s + (row[d] === 'T' ? 1 : 0), 0)
    );

    // Annual roster (T/L/V/F)
    const annualGrid: string[][] = [];
    for (let emp = 0; emp < headcount; emp++) {
      const offset = Math.round(emp * staggerStep) % cycleLen;
      const row: string[] = [];
      for (let d = 0; d < yearDays; d++) {
        row.push(((d + offset) % cycleLen) < workDays ? 'T' : 'L');
      }
      annualGrid.push(row);
    }
    const annualCoverage = Array.from({ length: yearDays }, (_, d) =>
      annualGrid.reduce((s, row) => s + (row[d] === 'T' ? 1 : 0), 0)
    );

    // vacPerPerson and trainPerPerson already declared above with adosaOverride support
    const empStats: { employee: number; work_days: number; off_days: number; vacation_days: number; training_days: number }[] = [];

    for (let emp = 0; emp < headcount; emp++) {
      let vInjected = 0;
      let fInjected = 0;
      const candidates: { day: number; surplus: number }[] = [];
      for (let d = 0; d < yearDays; d++) {
        if (annualGrid[emp][d] === 'T' && annualCoverage[d] > dsr) {
          candidates.push({ day: d, surplus: annualCoverage[d] - dsr });
        }
      }
      candidates.sort((a, b) => b.surplus - a.surplus);

      for (const c of candidates) {
        if (vInjected >= vacPerPerson) break;
        if (annualGrid[emp][c.day] === 'T' && annualCoverage[c.day] > dsr) {
          annualGrid[emp][c.day] = 'V';
          annualCoverage[c.day]--;
          vInjected++;
        }
      }
      for (const c of candidates) {
        if (fInjected >= trainPerPerson) break;
        if (annualGrid[emp][c.day] === 'T' && annualCoverage[c.day] > dsr) {
          annualGrid[emp][c.day] = 'F';
          annualCoverage[c.day]--;
          fInjected++;
        }
      }

      empStats.push({
        employee: emp + 1,
        work_days: annualGrid[emp].filter(c => c === 'T').length,
        off_days: annualGrid[emp].filter(c => c === 'L').length,
        vacation_days: vInjected,
        training_days: fInjected,
      });
    }

    const mStarts = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
    const mLens = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    const mNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthsSummary = mNames.map((name, i) => {
      const slice = annualCoverage.slice(mStarts[i], mStarts[i] + mLens[i]);
      return {
        month: name,
        avg_coverage: slice.length > 0 ? Math.round(slice.reduce((a, b) => a + b, 0) / slice.length * 10) / 10 : 0,
        min_coverage: slice.length > 0 ? Math.min(...slice) : 0,
        max_coverage: slice.length > 0 ? Math.max(...slice) : 0,
        days_below_dsr: slice.filter(c => c < dsr).length,
      };
    });

    return {
      dsr,
      headcount,
      workDays,
      offDays,
      cycleLen,
      monthly: {
        roster: monthlyGrid,
        daily_coverage: monthlyCoverage,
        coverage_ok: monthlyCoverage.every(c => c >= dsr),
        month_days: monthDays,
      },
      annual: {
        coverage_ok: annualCoverage.every(c => c >= dsr),
        employee_stats: empStats,
        months_summary: monthsSummary,
        daily_coverage: annualCoverage,
      },
    };
  }, [project, adosaOverride, adosaParams]);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* HEADER */}
      <div className={`p-5 rounded-xl border ${cardBg}`}>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h3 className={`font-bold text-lg ${textMain}`}>ADOSA — Apron Dynamic Operational Staffing Analysis</h3>
            <p className={`text-sm ${textSub}`}>{language === 'es' ? 'Análisis de dotación basado en el NOP calculado por AP-CAM' : 'Staffing analysis based on NOP calculated by AP-CAM'}</p>
          </div>
          <div
            className="flex items-center gap-3 p-3 rounded-lg border-2 border-dashed"
            style={{ background: 'color-mix(in srgb, var(--aipron-info) 10%, transparent)', borderColor: 'color-mix(in srgb, var(--aipron-info) 35%, transparent)' }}
          >
            <span className={`text-xs font-bold uppercase ${textSub}`}>NOP</span>
            <span className={`text-3xl font-black ${textMain}`}>{apcas.nop_required || 0}</span>
            <span className={`text-xs ${textSub}`}>{language === 'es' ? 'posiciones' : 'positions'}</span>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* ADOSA STAFFING PARAMETERS — inline configuration                */}
      {/* ════════════════════════════════════════════════════════════════ */}
      <div className={`rounded-xl border ${cardBg} overflow-hidden`}>
        <button
          onClick={() => setAdosaParamsOpen(prev => !prev)}
          className={`w-full p-4 flex items-center justify-between text-left hover:bg-[var(--surface-raised)] transition-colors`}
        >
          <div className="flex items-center gap-3">
            <Settings size={16} className="text-[var(--aipron-accent)]" />
            <span className={`font-bold text-sm ${textMain}`}>
              {language === 'es' ? 'Parámetros de Dotación' : 'Staffing Parameters'}
            </span>
            <span
              className="text-xs px-2 py-0.5 rounded-full"
              style={{ background: 'var(--aipron-accent-muted)', color: 'var(--aipron-accent)' }}
            >
              {language === 'es' ? 'Configurar aquí' : 'Configure here'}
            </span>
          </div>
          <ChevronDown size={16} className={`${textSub} transition-transform ${adosaParamsOpen ? 'rotate-180' : ''}`} />
        </button>

        {adosaParamsOpen && (
          <div className="p-5 pt-2 space-y-5 border-t border-[var(--border)]/30">
            {/* Row 1: Shift Cycles */}
            <div>
              <p className="text-xs font-bold mb-3 uppercase flex items-center gap-2" style={{ color: 'var(--aipron-accent)' }}>
                <span className="px-2 py-0.5 rounded" style={{ background: 'var(--aipron-accent-muted)' }}>1</span>
                {language === 'es' ? 'Tipos de Turno (Ciclos)' : 'Shift Cycle Types'}
              </p>
              <div
                className="rounded-lg border p-3"
                style={{ borderColor: 'color-mix(in srgb, var(--aipron-accent) 25%, transparent)', background: 'color-mix(in srgb, var(--aipron-accent) 5%, transparent)' }}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-[10px] font-bold uppercase ${textSub}`}>
                    {language === 'es' ? 'Ciclos configurados' : 'Configured cycles'}
                  </span>
                  <button type="button"
                    onClick={() => {
                      const cycles = [...(adosaParams.shift_cycles || [])];
                      cycles.push({
                        name: `Turno ${cycles.length + 1}`, work_days: 6, off_days: 4,
                        shift_schedule: [
                          { name: 'M', hours: 8, staff: 1 },
                          { name: 'T', hours: 8, staff: 1 },
                          { name: 'N', hours: 0, staff: 0 },
                          { name: 'G', hours: 24, staff: 1 },
                        ],
                      });
                      handleAdosaParamChange('shift_cycles', cycles);
                    }}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-bold rounded transition-colors hover:opacity-80"
                    style={{ background: 'var(--aipron-accent-muted)', color: 'var(--aipron-accent)' }}
                  >
                    <Plus size={12} /> {language === 'es' ? 'Añadir ciclo' : 'Add cycle'}
                  </button>
                </div>

                {(adosaParams.shift_cycles || []).map((cycle: any, idx: number) => (
                  <div
                    key={idx}
                    className="mb-3 p-3 rounded-lg border bg-[var(--surface-raised)]"
                    style={{ borderColor: 'color-mix(in srgb, var(--aipron-accent) 15%, transparent)' }}
                  >
                    {/* Cycle Header: name + work/off days */}
                    <div className="flex items-center gap-2 mb-3">
                      <input type="text"
                        className={`p-1.5 rounded border outline-none text-xs font-bold ${inputBg} w-24`}
                        style={{ borderColor: 'color-mix(in srgb, var(--aipron-accent) 25%, transparent)' }}
                        value={cycle.name}
                        onChange={e => {
                          const c = [...(adosaParams.shift_cycles || [])];
                          c[idx] = { ...c[idx], name: e.target.value };
                          handleAdosaParamChange('shift_cycles', c);
                        }}
                      />
                      <span className={`text-[10px] ${textSub}`}>{language === 'es' ? 'Trabajo' : 'Work'}:</span>
                      <input type="number" step="1" min="1" max="15"
                        className={`p-1.5 rounded border outline-none text-xs text-center w-14 ${inputBg}`}
                        style={{ borderColor: 'color-mix(in srgb, var(--aipron-accent) 25%, transparent)' }}
                        value={cycle.work_days}
                        onChange={e => {
                          const c = [...(adosaParams.shift_cycles || [])];
                          c[idx] = { ...c[idx], work_days: parseFloat(e.target.value) || 1 };
                          handleAdosaParamChange('shift_cycles', c);
                        }}
                      />
                      <span className={`text-[10px] ${textSub}`}>{language === 'es' ? 'Libres' : 'Off'}:</span>
                      <input type="number" step="1" min="1" max="15"
                        className={`p-1.5 rounded border outline-none text-xs text-center w-14 ${inputBg}`}
                        style={{ borderColor: 'color-mix(in srgb, var(--aipron-accent) 25%, transparent)' }}
                        value={cycle.off_days}
                        onChange={e => {
                          const c = [...(adosaParams.shift_cycles || [])];
                          c[idx] = { ...c[idx], off_days: parseFloat(e.target.value) || 1 };
                          handleAdosaParamChange('shift_cycles', c);
                        }}
                      />
                      <span className="text-xs font-bold text-[var(--aipron-accent)]">
                        = {(cycle.work_days || 0) + (cycle.off_days || 0)}d
                      </span>
                      <div className="ml-auto flex items-center gap-1">
                        {(adosaParams.shift_cycles || []).length > 1 && (
                          <button type="button"
                            onClick={() => {
                              const c = [...(adosaParams.shift_cycles || [])];
                              c.splice(idx, 1);
                              handleAdosaParamChange('shift_cycles', c);
                            }}
                            className="p-1 rounded hover:opacity-80 cursor-pointer"
                            style={{ color: 'var(--aipron-danger)' }}
                          >
                            <Trash size={12} />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Shift Schedule Table (AVERAGE HOURS PER SHIFT) */}
                    <div className="rounded border border-[var(--border)] p-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-[10px] font-bold uppercase ${textSub}`}>
                          {language === 'es' ? 'Distribución de Turnos' : 'Shift Schedule'}
                        </span>
                        <button type="button"
                          onClick={() => {
                            const c = [...(adosaParams.shift_cycles || [])];
                            const sched = [...(c[idx].shift_schedule || [])];
                            sched.push({ name: `T${sched.length + 1}`, hours: 8, staff: 0 });
                            c[idx] = { ...c[idx], shift_schedule: sched };
                            handleAdosaParamChange('shift_cycles', c);
                          }}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--surface-inset)] text-[var(--text-secondary)] hover:opacity-80"
                        >
                          <Plus size={10} className="inline" /> {language === 'es' ? 'Tipo' : 'Type'}
                        </button>
                      </div>
                      {/* Header */}
                      <div className="grid grid-cols-[60px_60px_60px_70px_20px] gap-1 mb-0.5">
                        <span className={`text-[9px] font-bold uppercase ${textSub}`}>{language === 'es' ? 'Tipo' : 'Type'}</span>
                        <span className={`text-[9px] font-bold uppercase text-center ${textSub}`}>{language === 'es' ? 'Horas' : 'Hours'}</span>
                        <span className={`text-[9px] font-bold uppercase text-center ${textSub}`}>{language === 'es' ? 'Pers' : 'Staff'}</span>
                        <span className={`text-[9px] font-bold uppercase text-center ${textSub}`}>H/día</span>
                        <span></span>
                      </div>
                      {(cycle.shift_schedule || []).map((sh: any, sIdx: number) => {
                        const hrs = parseFloat(sh.hours) || 0;
                        const stf = parseFloat(sh.staff) || 0;
                        return (
                          <div key={sIdx} className="grid grid-cols-[60px_60px_60px_70px_20px] gap-1 mb-0.5 items-center">
                            <input type="text"
                              className={`p-1 rounded border outline-none text-[11px] ${inputBg} border-[var(--border)]/20 w-full`}
                              value={sh.name}
                              onChange={e => {
                                const c = [...(adosaParams.shift_cycles || [])];
                                const s = [...(c[idx].shift_schedule || [])];
                                s[sIdx] = { ...s[sIdx], name: e.target.value };
                                c[idx] = { ...c[idx], shift_schedule: s };
                                handleAdosaParamChange('shift_cycles', c);
                              }}
                            />
                            <input type="number" step="1" min="0" max="24"
                              className={`p-1 rounded border outline-none text-[11px] text-center ${inputBg} border-[var(--border)]/20 w-full`}
                              value={sh.hours}
                              onChange={e => {
                                const c = [...(adosaParams.shift_cycles || [])];
                                const s = [...(c[idx].shift_schedule || [])];
                                s[sIdx] = { ...s[sIdx], hours: parseFloat(e.target.value) || 0 };
                                c[idx] = { ...c[idx], shift_schedule: s };
                                handleAdosaParamChange('shift_cycles', c);
                              }}
                            />
                            <input type="number" step="1" min="0" max="10"
                              className={`p-1 rounded border outline-none text-[11px] text-center font-bold ${inputBg} border-[var(--border)]/20 w-full`}
                              value={sh.staff}
                              onChange={e => {
                                const c = [...(adosaParams.shift_cycles || [])];
                                const s = [...(c[idx].shift_schedule || [])];
                                s[sIdx] = { ...s[sIdx], staff: parseFloat(e.target.value) || 0 };
                                c[idx] = { ...c[idx], shift_schedule: s };
                                handleAdosaParamChange('shift_cycles', c);
                              }}
                            />
                            <div className={`text-[11px] text-center ${textSub}`}>{hrs * stf}</div>
                            <button type="button"
                              onClick={() => {
                                const c = [...(adosaParams.shift_cycles || [])];
                                const s = [...(c[idx].shift_schedule || [])];
                                if (s.length > 1) { s.splice(sIdx, 1); c[idx] = { ...c[idx], shift_schedule: s }; handleAdosaParamChange('shift_cycles', c); }
                              }}
                              className={`text-[10px] ${(cycle.shift_schedule || []).length <= 1 ? 'opacity-20' : 'hover:opacity-80 cursor-pointer'} p-0.5 rounded`}
                              style={(cycle.shift_schedule || []).length <= 1 ? undefined : { color: 'var(--aipron-danger)' }}
                              disabled={(cycle.shift_schedule || []).length <= 1}
                            >
                              <Trash size={10} />
                            </button>
                          </div>
                        );
                      })}
                      {/* DSR_real summary */}
                      <div className="mt-1 pt-1 border-t border-[var(--border)] flex justify-between text-[11px] font-bold">
                        <span className="text-[var(--aipron-accent)]">
                          DSR real = {(cycle.shift_schedule || []).reduce((s: number, sh: any) => s + (parseFloat(sh.staff) || 0), 0)}
                        </span>
                        <span className={textSub}>
                          Avg: {(() => {
                            const sched = cycle.shift_schedule || [];
                            const dsr = sched.reduce((s: number, sh: any) => s + (parseFloat(sh.staff) || 0), 0);
                            const total = sched.reduce((s: number, sh: any) => s + (parseFloat(sh.hours) || 0) * (parseFloat(sh.staff) || 0), 0);
                            return dsr > 0 ? (total / dsr).toFixed(1) : '0';
                          })()} h
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Row 2: Vacation & Bank Holidays */}
            <div className="pt-3 border-t border-[var(--border)]/20">
              <p className="text-xs font-bold mb-3 uppercase flex items-center gap-2" style={{ color: 'var(--aipron-info)' }}>
                <span className="px-2 py-0.5 rounded" style={{ background: 'color-mix(in srgb, var(--aipron-info) 15%, transparent)' }}>2</span>
                {language === 'es' ? 'Vacaciones y Festivos' : 'Vacation & Holidays'}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <label className={labelStyle}>{language === 'es' ? 'Días Vacaciones' : 'Vacation Days'}</label>
                  <input type="number" step="1" min="0" max="60"
                    className={`w-full p-2 rounded border outline-none ${inputBg}`}
                    value={adosaParams.holidays_vacation_days || 30}
                    onChange={e => handleAdosaParamChange('holidays_vacation_days', e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelStyle}>{language === 'es' ? 'Tipo vacaciones' : 'Vacation type'}</label>
                  <div className="flex gap-1 mt-0.5">
                    <button type="button"
                      onClick={() => handleAdosaParamChange('is_calendar_days', false)}
                      className={`flex-1 p-2 text-xs rounded border transition-all ${!adosaParams.is_calendar_days ? 'bg-[var(--aipron-accent)] border-[var(--aipron-accent)] text-white font-bold' : `border-[var(--border)] hover:bg-[var(--surface-raised)] ${textSub}`}`}
                    >
                      {language === 'es' ? 'Laborables' : 'Working'}
                    </button>
                    <button type="button"
                      onClick={() => handleAdosaParamChange('is_calendar_days', true)}
                      className={`flex-1 p-2 text-xs rounded border transition-all ${adosaParams.is_calendar_days ? 'bg-[var(--aipron-accent)] border-[var(--aipron-accent)] text-white font-bold' : `border-[var(--border)] hover:bg-[var(--surface-raised)] ${textSub}`}`}
                    >
                      {language === 'es' ? 'Naturales' : 'Calendar'}
                    </button>
                  </div>
                </div>
                <div>
                  <label className={labelStyle}>{language === 'es' ? 'Festivos / Año' : 'Bank Holidays / Year'}</label>
                  <input type="number" step="1" min="0" max="30"
                    className={`w-full p-2 rounded border outline-none ${inputBg}`}
                    value={adosaParams.bank_holidays_days ?? 0}
                    onChange={e => handleAdosaParamChange('bank_holidays_days', e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelStyle}>{language === 'es' ? 'Permisos laborales' : 'Work Permits'}</label>
                  <input type="number" step="1" min="0" max="20"
                    className={`w-full p-2 rounded border outline-none ${inputBg}`}
                    value={adosaParams.work_permits_days ?? 0}
                    onChange={e => handleAdosaParamChange('work_permits_days', e.target.value)}
                  />
                  <p className="text-xs text-[var(--text-tertiary)] mt-1">{language === 'es' ? 'Días/año' : 'Days/year'}</p>
                </div>
              </div>
            </div>

            {/* Row 3: Training & Absenteeism */}
            <div className="pt-3 border-t border-[var(--border)]/20">
              <p className="text-xs font-bold mb-3 uppercase flex items-center gap-2" style={{ color: 'var(--aipron-accent)' }}>
                <span className="px-2 py-0.5 rounded" style={{ background: 'var(--aipron-accent-muted)' }}>3</span>
                {language === 'es' ? 'Formación y Absentismo' : 'Training & Absenteeism'}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <label className={labelStyle}>{language === 'es' ? 'Formación anual (h)' : 'Annual Training (h)'}</label>
                  <input type="number" step="1" min="0" max="200"
                    className={`w-full p-2 rounded border outline-none ${inputBg}`}
                    style={{ borderColor: 'color-mix(in srgb, var(--aipron-accent) 30%, transparent)' }}
                    value={adosaParams.yearly_training_hours ?? 60}
                    onChange={e => handleAdosaParamChange('yearly_training_hours', e.target.value)}
                  />
                  <p className="text-xs text-[var(--text-tertiary)] mt-1">{language === 'es' ? 'Horas/persona/año' : 'Hours/person/year'}</p>
                </div>
                <div>
                  <label className={labelStyle}>{language === 'es' ? 'Turno formación (h)' : 'Training shift (h)'}</label>
                  <input type="number" step="0.5" min="1" max="12"
                    className={`w-full p-2 rounded border outline-none ${inputBg}`}
                    style={{ borderColor: 'color-mix(in srgb, var(--aipron-accent) 30%, transparent)' }}
                    value={adosaParams.training_shift_duration ?? 8}
                    onChange={e => handleAdosaParamChange('training_shift_duration', e.target.value)}
                  />
                  <p className="text-xs text-[var(--text-tertiary)] mt-1">
                    = {Math.ceil((parseFloat(adosaParams.yearly_training_hours) || 20) / (parseFloat(adosaParams.training_shift_duration) || 8))} {language === 'es' ? 'días formación' : 'training days'}
                  </p>
                </div>
                <div>
                  <label className={labelStyle}>{language === 'es' ? 'Absentismo (%)' : 'Absenteeism (%)'}</label>
                  <input type="number" step="0.5" min="0" max="30"
                    className={`w-full p-2 rounded border outline-none ${inputBg}`}
                    style={{ borderColor: 'color-mix(in srgb, var(--aipron-danger) 30%, transparent)' }}
                    value={adosaParams.absenteeism_pct ?? 5}
                    onChange={e => handleAdosaParamChange('absenteeism_pct', e.target.value)}
                  />
                  <p className="text-xs mt-1" style={{ color: 'var(--aipron-danger)' }}>{language === 'es' ? '% anual esperado' : 'Expected annual %'}</p>
                </div>
                <div className="col-span-2">
                  <label className={labelStyle}>{language === 'es' ? 'Safety Buffer (%)' : 'Safety Buffer (%)'}</label>
                  <input type="number" step="1" min="0" max="30"
                    className={`w-full p-2 rounded border outline-none ${inputBg}`}
                    style={{ borderColor: 'color-mix(in srgb, var(--aipron-success) 30%, transparent)' }}
                    value={adosaParams.safety_buffer_pct ?? 0}
                    onChange={e => handleAdosaParamChange('safety_buffer_pct', e.target.value)}
                  />
                  <p className="text-xs mt-1" style={{ color: 'var(--aipron-success)' }}>{language === 'es' ? 'Buffer entre Min y Opt para nivel sin riesgo' : 'Buffer between Min and Optimal for risk-free level'}</p>
                </div>
              </div>
            </div>

            {/* Row 4: DSR — Towers & Positions */}
            <div className="pt-3 border-t border-[var(--border)]/20">
              <p className="text-xs font-bold mb-3 uppercase flex items-center gap-2" style={{ color: 'var(--aipron-info)' }}>
                <span className="px-2 py-0.5 rounded" style={{ background: 'color-mix(in srgb, var(--aipron-info) 15%, transparent)' }}>4</span>
                {language === 'es' ? 'DSR — Dependencias y Posiciones' : 'DSR — Towers & Positions'}
              </p>

              {/* Rest Factor — only input for DSR */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div>
                  <label className={labelStyle}>{language === 'es' ? 'Factor descanso (%)' : 'Rest Factor (%)'}</label>
                  <input type="number" step="1" min="0" max="50" className={`w-full p-2 rounded border outline-none ${inputBg}`}
                    value={adosaParams.rest_factor_pct ?? 0} onChange={e => handleAdosaParamChange('rest_factor_pct', e.target.value)}
                  />
                  <p className="text-xs text-[var(--text-tertiary)] mt-1">{language === 'es' ? '% sobre turno' : '% over shift'}</p>
                </div>
              </div>

              {/* Towers */}
              {(adosaParams.towers || []).map((tower: any, tIdx: number) => (
                <div
                  key={tIdx}
                  className="rounded-lg border p-3 mb-3"
                  style={{ borderColor: 'color-mix(in srgb, var(--aipron-info) 25%, transparent)', background: 'color-mix(in srgb, var(--aipron-info) 5%, transparent)' }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <input type="text"
                        className={`p-1 rounded border outline-none text-xs font-bold ${inputBg} w-32`}
                        style={{ borderColor: 'color-mix(in srgb, var(--aipron-info) 25%, transparent)' }}
                        value={tower.name}
                        onChange={e => {
                          const t = [...(adosaParams.towers || [])];
                          t[tIdx] = { ...t[tIdx], name: e.target.value };
                          handleAdosaParamChange('towers', t);
                        }}
                      />
                      {(adosaParams.towers || []).length > 1 && (
                        <button type="button"
                          onClick={() => {
                            const t = [...(adosaParams.towers || [])];
                            t.splice(tIdx, 1);
                            handleAdosaParamChange('towers', t);
                          }}
                          className="p-1 rounded hover:opacity-80 cursor-pointer"
                          style={{ color: 'var(--aipron-danger)' }}
                        >
                          <Trash size={12} />
                        </button>
                      )}
                    </div>
                    <button type="button"
                      onClick={() => {
                        const t = [...(adosaParams.towers || [])];
                        const positions = [...(t[tIdx].positions || [])];
                        positions.push({ name: `Pos ${positions.length + 1}`, schedule_hours: 0, num_shifts: 0, is_operational: true });
                        t[tIdx] = { ...t[tIdx], positions };
                        handleAdosaParamChange('towers', t);
                      }}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-bold rounded transition-colors hover:opacity-80"
                      style={{ background: 'color-mix(in srgb, var(--aipron-info) 15%, transparent)', color: 'var(--aipron-info)' }}
                    >
                      <Plus size={12} /> {language === 'es' ? 'Posición' : 'Position'}
                    </button>
                  </div>

                  {/* Position headers */}
                  <div className="grid grid-cols-[40px_1fr_70px_60px_60px_60px_32px] gap-1.5 mb-1">
                    <span className={`text-[10px] font-bold uppercase text-center ${textSub}`}>{language === 'es' ? 'Tipo' : 'Type'}</span>
                    <span className={`text-[10px] font-bold uppercase ${textSub}`}>{language === 'es' ? 'Posición' : 'Position'}</span>
                    <span className={`text-[10px] font-bold uppercase text-center ${textSub}`}>{language === 'es' ? 'Horario' : 'Schedule'}</span>
                    <span className={`text-[10px] font-bold uppercase text-center ${textSub}`}>{language === 'es' ? 'Turnos' : 'Shifts'}</span>
                    <span className={`text-[10px] font-bold uppercase text-center ${textSub}`}>{language === 'es' ? 'Pers/T' : 'Staff/S'}</span>
                    <span className={`text-[10px] font-bold uppercase text-center ${textSub}`}>Total</span>
                    <span></span>
                  </div>

                  {/* Position rows */}
                  {(tower.positions || []).map((pos: any, pIdx: number) => {
                    const schedH = parseFloat(pos.schedule_hours) || 0;
                    const nShifts = parseFloat(pos.num_shifts) || 0;
                    const restF = parseFloat(adosaParams.rest_factor_pct) || 0;
                    const staffReq = nShifts > 0 ? 1 + restF / 100 : 0;
                    const totalStaff = staffReq * nShifts;
                    const isOp = pos.is_operational !== false;
                    return (
                      <div key={pIdx} className={`grid grid-cols-[40px_1fr_70px_60px_60px_60px_32px] gap-1.5 mb-1 items-center ${!isOp ? 'opacity-60' : ''}`}>
                        <button type="button"
                          onClick={() => {
                            const t = [...(adosaParams.towers || [])];
                            const p = [...(t[tIdx].positions || [])];
                            p[pIdx] = { ...p[pIdx], is_operational: !isOp };
                            t[tIdx] = { ...t[tIdx], positions: p };
                            handleAdosaParamChange('towers', t);
                          }}
                          className="px-1 py-0.5 rounded text-[9px] font-black cursor-pointer transition-colors border"
                          style={isOp
                            ? { background: 'color-mix(in srgb, var(--aipron-success) 15%, transparent)', color: 'var(--aipron-success)', borderColor: 'color-mix(in srgb, var(--aipron-success) 30%, transparent)' }
                            : { background: 'color-mix(in srgb, var(--aipron-warning) 15%, transparent)', color: 'var(--aipron-warning)', borderColor: 'color-mix(in srgb, var(--aipron-warning) 30%, transparent)' }
                          }
                          title={isOp ? (language === 'es' ? 'Posición operativa (click para cambiar)' : 'Operational (click to toggle)') : (language === 'es' ? 'Supervisor / No operativa (click para cambiar)' : 'Supervisor / Non-operational (click to toggle)')}
                        >
                          {isOp ? 'OP' : 'SUP'}
                        </button>
                        <input type="text"
                          className={`p-1.5 rounded border outline-none text-xs ${inputBg}`}
                          style={{ borderColor: 'color-mix(in srgb, var(--aipron-info) 25%, transparent)' }}
                          value={pos.name}
                          onChange={e => {
                            const t = [...(adosaParams.towers || [])];
                            const p = [...(t[tIdx].positions || [])];
                            p[pIdx] = { ...p[pIdx], name: e.target.value };
                            t[tIdx] = { ...t[tIdx], positions: p };
                            handleAdosaParamChange('towers', t);
                          }}
                        />
                        <input type="number" step="1" min="0" max="24"
                          className={`p-1.5 rounded border outline-none text-xs text-center ${inputBg}`}
                          style={{ borderColor: 'color-mix(in srgb, var(--aipron-info) 25%, transparent)' }}
                          value={pos.schedule_hours}
                          onChange={e => {
                            const t = [...(adosaParams.towers || [])];
                            const p = [...(t[tIdx].positions || [])];
                            p[pIdx] = { ...p[pIdx], schedule_hours: parseFloat(e.target.value) || 0 };
                            t[tIdx] = { ...t[tIdx], positions: p };
                            handleAdosaParamChange('towers', t);
                          }}
                        />
                        <input type="number" step="1" min="0" max="8"
                          className={`p-1.5 rounded border outline-none text-xs text-center ${inputBg}`}
                          style={{ borderColor: 'color-mix(in srgb, var(--aipron-info) 25%, transparent)' }}
                          value={pos.num_shifts}
                          onChange={e => {
                            const t = [...(adosaParams.towers || [])];
                            const p = [...(t[tIdx].positions || [])];
                            p[pIdx] = { ...p[pIdx], num_shifts: parseFloat(e.target.value) || 0 };
                            t[tIdx] = { ...t[tIdx], positions: p };
                            handleAdosaParamChange('towers', t);
                          }}
                        />
                        <div className={`text-xs text-center ${textSub}`}>
                          {staffReq > 0 ? staffReq.toFixed(2) : '-'}
                        </div>
                        <div className="text-xs text-center font-black" style={{ color: totalStaff > 0 ? 'var(--aipron-info)' : 'var(--text-secondary)' }}>
                          {totalStaff > 0 ? totalStaff.toFixed(1) : '0'}
                        </div>
                        <button type="button"
                          onClick={() => {
                            const t = [...(adosaParams.towers || [])];
                            const p = [...(t[tIdx].positions || [])];
                            if (p.length > 1) { p.splice(pIdx, 1); t[tIdx] = { ...t[tIdx], positions: p }; handleAdosaParamChange('towers', t); }
                          }}
                          className={`p-1 rounded text-xs transition-colors ${(tower.positions || []).length <= 1 ? 'opacity-30 cursor-not-allowed' : 'hover:opacity-80 cursor-pointer'}`}
                          style={(tower.positions || []).length <= 1 ? undefined : { color: 'var(--aipron-danger)' }}
                          disabled={(tower.positions || []).length <= 1}
                        >
                          <Trash size={12} />
                        </button>
                      </div>
                    );
                  })}

                  {/* Tower DSR — separated OP vs SUP */}
                  <div className="mt-2 pt-2 border-t text-xs font-bold" style={{ borderColor: 'color-mix(in srgb, var(--aipron-info) 20%, transparent)' }}>
                    {(() => {
                      const restF = parseFloat(adosaParams.rest_factor_pct) || 0;
                      const sr = 1 + restF / 100;
                      const opDSR = (tower.positions || []).filter((p: any) => p.is_operational !== false).reduce((s: number, p: any) => s + sr * (parseFloat(p.num_shifts) || 0), 0);
                      const supDSR = (tower.positions || []).filter((p: any) => p.is_operational === false).reduce((s: number, p: any) => s + sr * (parseFloat(p.num_shifts) || 0), 0);
                      return (
                        <div className="flex gap-4 flex-wrap">
                          <span style={{ color: 'var(--aipron-info)' }}>
                            DSR OP {tower.name} = {opDSR.toFixed(1)}
                          </span>
                          {supDSR > 0 && (
                            <span className="text-[var(--aipron-warning)]">
                              DSR SUP = {supDSR.toFixed(1)}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ))}

              {/* Add Tower button */}
              <button type="button"
                onClick={() => {
                  const t = [...(adosaParams.towers || [])];
                  t.push({ name: `Apron ${t.length + 1}`, positions: [{ name: 'CWP 1', schedule_hours: 0, num_shifts: 0 }] });
                  handleAdosaParamChange('towers', t);
                }}
                className="w-full p-2 text-xs font-bold rounded-lg border-2 border-dashed transition-colors hover:opacity-80"
                style={{ borderColor: 'color-mix(in srgb, var(--aipron-info) 25%, transparent)', color: 'var(--aipron-info)' }}
              >
                <Plus size={12} className="inline mr-1" />
                {language === 'es' ? 'Añadir Dependencia (Torre)' : 'Add Tower'}
              </button>

              {/* Global DSR Summary */}
              <div
                className="mt-3 p-3 rounded-lg text-xs font-bold border"
                style={{ background: 'color-mix(in srgb, var(--aipron-info) 10%, transparent)', color: 'var(--aipron-info)', borderColor: 'color-mix(in srgb, var(--aipron-info) 20%, transparent)' }}
              >
                <div className="flex flex-wrap gap-4 items-center">
                  <span className="text-lg">
                    DSR base = {(() => {
                      const restF = parseFloat(adosaParams.rest_factor_pct) || 0;
                      const sr = 1 + restF / 100;
                      const total = (adosaParams.towers || []).reduce((s: number, tw: any) =>
                        s + (tw.positions || []).reduce((s2: number, p: any) =>
                          s2 + sr * (parseFloat(p.num_shifts) || 0), 0), 0);
                      return total.toFixed(1);
                    })()}
                  </span>
                  <span className={textSub}>
                    {language === 'es' ? '(DSR real se calcula en la sección de turnos)' : '(DSR real computed in shift schedule)'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>


      {/* Recalculate Button */}
      <div className="flex items-center gap-4">
        <button
          onClick={recalculateStaffing}
          disabled={adosaRecalculating}
          className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-bold text-sm transition-all shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-95 ${adosaRecalculating
            ? 'bg-[var(--surface-inset)] text-[var(--text-tertiary)] cursor-wait'
            : 'bg-gradient-to-r from-[var(--aipron-accent)] to-[var(--aipron-accent-hover)] hover:opacity-90 text-white'
            }`}
        >
          {adosaRecalculating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          {language === 'es' ? 'Recalcular Dotación' : 'Recalculate Staffing'}
        </button>
        {adosaOverride && (
          <span
            className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border"
            style={{ background: 'color-mix(in srgb, var(--aipron-success) 10%, transparent)', color: 'var(--aipron-success)', borderColor: 'color-mix(in srgb, var(--aipron-success) 30%, transparent)' }}
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            {language === 'es' ? 'Recalculado con parámetros actuales' : 'Recalculated with current params'}
          </span>
        )}
      </div>

      {/* Staffing Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard title={language === 'es' ? 'DSR (Diario)' : 'DSR (Daily)'} value={`${adosa.dsr_real || 0}`} sub={language === 'es' ? 'Requerimiento diario' : 'Daily requirement'} icon={Users} isDark={isDark} color="blue" />
        <MetricCard title="Optimal Staffing" value={`${adosa.optimal_staffing || 0}`} sub={language === 'es' ? 'Con márgenes' : 'With margins'} icon={CheckCircle2} isDark={isDark} color="green" />
        <MetricCard title="Minimum Staffing" value={`${adosa.minimum_staffing || 0}`} sub={language === 'es' ? 'Mínimo aceptable' : 'Acceptable min.'} icon={Clock} isDark={isDark} color="yellow" />
        <MetricCard title="Risk-Free Staffing" value={`${adosa.risk_free_staffing || 0}`} sub={language === 'es' ? 'Sin riesgo' : 'No risk'} icon={Activity} isDark={isDark} color="orange" />
      </div>

      {/* Bar Chart */}
      <div className={`p-6 rounded-xl border ${cardBg}`}>
        <h3 className={`font-bold text-sm mb-4 ${textMain}`}>{language === 'es' ? 'Comparativa de Dotación' : 'Staffing Comparison'}</h3>
        <div className="h-[220px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={[
              { name: language === 'es' ? 'Mínimo' : 'Minimum', value: adosa.minimum_staffing || 0 },
              { name: 'Optimal', value: adosa.optimal_staffing || 0 },
              { name: 'Risk-Free', value: adosa.risk_free_staffing || 0 },
              { name: 'DSR', value: adosa.dsr_real || 0 },
            ]} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--border)" />
              <XAxis type="number" stroke="var(--text-secondary)" fontSize={12} />
              <YAxis type="category" dataKey="name" stroke="var(--text-secondary)" fontSize={12} width={80} />
              <Tooltip contentStyle={{ backgroundColor: 'var(--surface-raised)', borderColor: 'var(--border)', borderRadius: '8px' }} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                {[{ fill: '#eab308' }, { fill: '#10b981' }, { fill: '#f97316' }, { fill: '#3b82f6' }].map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Shift Coverage + Annual Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className={`p-6 rounded-xl border ${cardBg}`}>
          <h3 className={`font-bold text-sm mb-1 ${textMain}`}>{language === 'es' ? 'Cobertura por Turno (24h)' : 'Shift Coverage (24h)'}</h3>
          <p className={`text-xs mb-4 ${textSub}`}>{language === 'es' ? 'Distribución estimada por turno' : 'Estimated distribution per shift'}</p>
          <div className="space-y-3">
            {[
              { shift: language === 'es' ? 'Mañana' : 'Morning', hours: '06:00-14:00', pct: 0.40, color: 'var(--aipron-warning)' },
              { shift: language === 'es' ? 'Tarde' : 'Afternoon', hours: '14:00-22:00', pct: 0.40, color: 'var(--aipron-info)' },
              { shift: language === 'es' ? 'Noche' : 'Night', hours: '22:00-06:00', pct: 0.20, color: 'var(--aipron-accent)' },
            ].map((s, idx) => {
              const positions = Math.ceil((apcas.nop_required || 0) * s.pct);
              return (
                <div key={idx}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className={`font-bold ${textMain}`}>{s.shift} <span className={textSub}>({s.hours})</span></span>
                    <span className={`font-bold ${textMain}`}>{positions} pos</span>
                  </div>
                  <div className="h-3 rounded-full overflow-hidden bg-[var(--surface-inset)]">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(s.pct * 100 * 2.5, 100)}%`, background: s.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className={`p-6 rounded-xl border ${cardBg}`}>
          <h3 className={`font-bold text-sm mb-1 ${textMain}`}>{language === 'es' ? 'Resumen de Dotación' : 'Staffing Summary'}</h3>
          <p className={`text-xs mb-4 ${textSub}`}>{language === 'es' ? 'FTEs anuales para cobertura completa' : 'Annual FTEs for full coverage'}</p>
          <div className="space-y-3">
            {(() => {
              const minStaff = adosa.minimum_staffing || 0;
              const optStaff = adosa.optimal_staffing || 0;
              const riskFree = adosa.risk_free_staffing || 0;
              const dsrReal = adosa.dsr_real || 0;
              const coverageRatio = optStaff > 0 && dsrReal > 0 ? ((optStaff / dsrReal) * 100) : 0;
              return (
                <>
                  <div className={`flex justify-between items-center p-3 rounded-lg ${'bg-[var(--surface-raised)]'}`}>
                    <div className={`text-sm ${textSub}`}>DSR ({language === 'es' ? 'diario' : 'daily'})</div>
                    <div className="text-xl font-black text-[var(--aipron-info)]">{dsrReal}</div>
                  </div>
                  <div className={`flex justify-between items-center p-3 rounded-lg ${'bg-[var(--surface-raised)]'}`}>
                    <div className={`text-sm ${textSub}`}>{language === 'es' ? 'Mínimo (FTE)' : 'Minimum (FTE)'}</div>
                    <div className="text-xl font-black text-[var(--aipron-info)]">{minStaff}</div>
                  </div>
                  <div
                    className="flex justify-between items-center p-3 rounded-lg"
                    style={{ background: 'color-mix(in srgb, var(--aipron-success) 8%, transparent)' }}
                  >
                    <div className={`text-sm font-bold ${textSub}`}>{language === 'es' ? '★ Óptimo (FTE)' : '★ Optimal (FTE)'}</div>
                    <div className="text-xl font-black text-[var(--aipron-success)]">{optStaff}</div>
                  </div>
                  <div
                    className="flex justify-between items-center p-3 rounded-lg"
                    style={{ background: 'var(--aipron-accent-muted)' }}
                  >
                    <div className={`flex items-center gap-1.5 text-sm font-bold ${textSub}`}><ShieldCheck size={14} className="text-[var(--aipron-accent)]" />{language === 'es' ? 'Risk-Free (FTE)' : 'Risk-Free (FTE)'}</div>
                    <div className="text-xl font-black text-[var(--aipron-accent)]">{riskFree}</div>
                  </div>
                  <div className={`flex justify-between items-center p-3 rounded-lg ${'bg-[var(--surface-raised)]'}`}>
                    <div className={`text-sm ${textSub}`}>{language === 'es' ? 'Ratio Cobertura' : 'Coverage Ratio'}</div>
                    <div className="text-xl font-black" style={{ color: coverageRatio >= 100 ? 'var(--aipron-success)' : 'var(--aipron-warning)' }}>{safeNum(coverageRatio, 0)}%</div>
                  </div>
                </>
              );
            })()}
          </div>
          <div className={`mt-4 p-3 rounded-lg border text-xs bg-[var(--surface-raised)] border-[var(--border)] ${textSub}`}>
            <strong>{language === 'es' ? 'Nota' : 'Note'}:</strong> {language === 'es' ? ' MS/OS/RFS son los FTEs permanentes necesarios para cubrir el servicio 365 días/año, incluyendo vacaciones, absentismo y formación.' : ' MS/OS/RFS are the permanent FTEs needed for 365-day coverage, including holidays, absenteeism & training.'}
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* SECTION 2: STAFFING RESULTS — PER CYCLE                       */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div className={`p-6 rounded-xl border ${cardBg}`}>
        <h3 className={`font-bold text-sm mb-1 ${textMain}`}>
          {language === 'es' ? 'Resultados del STAFFING TOOL — por Ciclo' : 'STAFFING TOOL Results — per Cycle'}
        </h3>
        <p className={`text-xs mb-5 ${textSub}`}>
          {language === 'es'
            ? 'Mínimo & Óptimo por ciclo de turno, con/sin Safety Correction Index'
            : 'Minimum & Optimal per shift cycle, with/without Safety Correction Index'}
        </p>

        {adosaOverride?.cycle_results && adosaOverride.cycle_results.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className={`border-b-2 ${'border-[var(--border)]'}`}>
                  <th className={`text-left py-2 px-2 font-bold ${textSub}`}>{language === 'es' ? 'Concepto' : 'Concept'}</th>
                  {adosaOverride.cycle_results.map((c: any, i: number) => (
                    <th key={i} className="text-center py-2 px-2 font-bold text-[var(--aipron-accent)]">{c.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { key: 'cycle_length', label: language === 'es' ? 'Ciclo (T+L)' : 'Cycle (W+O)', fmt: (v: number) => `${v}d` },
                  { key: 'shift_pattern', label: language === 'es' ? 'Ciclos/año' : 'Cycles/year', fmt: (v: number) => v.toFixed(2) },
                  { key: 'dsr_real', label: 'DSR real', fmt: (v: number) => v.toString(), bold: true, color: 'text-[var(--aipron-info)]' },
                  { key: 'available_working_days', label: language === 'es' ? 'Días disponibles' : 'Available working days', fmt: (v: number) => v.toString() },
                  { key: 'yearly_working_days', label: language === 'es' ? 'Días laborales' : 'Yearly working days', fmt: (v: number) => v.toString() },
                  { key: 'absenteeism_days', label: language === 'es' ? 'Absentismo (días)' : 'Absenteeism (days)', fmt: (v: number) => v.toString(), color: 'text-[var(--aipron-danger)]' },
                  { key: 'total_on_duty', label: language === 'es' ? 'Total días en servicio' : 'Total days on-duty', fmt: (v: number) => v.toString(), bold: true },
                  { key: 'training_days', label: language === 'es' ? 'Formación (días)' : 'Training (days)', fmt: (v: number) => v.toString(), color: 'text-[var(--aipron-accent)]' },
                  { key: 'total_annual_shifts', label: language === 'es' ? 'DSR × 365' : 'DSR × 365', fmt: (v: number) => v.toString() },
                  { key: 'absenteeism_safety_idx', label: language === 'es' ? 'Índice Absentismo' : 'Absenteeism Index', fmt: (v: number) => v.toFixed(4), color: 'text-[var(--aipron-danger)]' },
                  { key: 'training_safety_idx', label: language === 'es' ? 'Índice Formación' : 'Training Index', fmt: (v: number) => v.toFixed(4), color: 'text-[var(--aipron-accent)]' },
                  { key: 'safety_correction_idx', label: language === 'es' ? 'Safety Correction Index' : 'Safety Correction Index', fmt: (v: number) => v.toFixed(4), bold: true, color: 'text-[var(--aipron-warning)]' },
                  { key: 'total_equiv_working_days', label: language === 'es' ? 'Total Equiv. Sizing' : 'Total Equiv. Sizing', fmt: (v: number) => v.toString() },
                  { key: 'avg_hours', label: language === 'es' ? 'Horas promedio' : 'Average hours', fmt: (v: number) => v.toFixed(1) },
                  { key: 'annual_hours', label: language === 'es' ? 'Horas anuales' : 'Annual hours', fmt: (v: number) => v.toString() },
                  { key: 'monthly_hours', label: language === 'es' ? 'Horas mensuales' : 'Monthly hours', fmt: (v: number) => v.toString() },
                ].map((row, rIdx) => (
                  <tr key={rIdx} className="border-b border-[var(--border-subtle)]">
                    <td className={`py-1.5 px-2 ${row.bold ? 'font-bold' : ''} ${(row as any).color || textSub}`}>{row.label}</td>
                    {adosaOverride.cycle_results.map((c: any, cIdx: number) => (
                      <td key={cIdx} className={`py-1.5 px-2 text-center ${row.bold ? `font-bold ${textMain}` : textMain}`}>
                        {row.fmt(c[row.key] || 0)}
                      </td>
                    ))}
                  </tr>
                ))}

                {/* Separator */}
                <tr><td colSpan={1 + adosaOverride.cycle_results.length} className={`py-1 ${'bg-[var(--surface-raised)]'}`}></td></tr>

                {/* STAFFING RESULTS */}
                {[
                  { key: 'minimum_staff', label: language === 'es' ? '✦ MINIMUM STAFF' : '✦ MINIMUM STAFF', color: 'text-[var(--aipron-info)]', bg: '' },
                  { key: 'min_staff_no_sci', label: language === 'es' ? '  Min w/o SCI' : '  Min w/o SCI', color: 'text-[var(--text-secondary)]', bg: '' },
                  { key: 'optimal_staff', label: language === 'es' ? '★ OPTIMAL STAFF' : '★ OPTIMAL STAFF', color: 'text-[var(--aipron-success)]', bg: 'success' },
                  { key: 'opt_staff_no_sci', label: language === 'es' ? '  Opt w/o SCI' : '  Opt w/o SCI', color: 'text-[var(--text-secondary)]', bg: '' },
                  { key: 'risk_free_staff', label: language === 'es' ? 'RISK-FREE (buffer)' : 'RISK-FREE (buffer)', color: 'text-[var(--aipron-accent)]', bg: 'accent' },
                ].map((row, rIdx) => (
                  <tr
                    key={`staff-${rIdx}`}
                    className="border-b border-[var(--border-subtle)]"
                    style={
                      row.bg === 'success' ? { background: 'color-mix(in srgb, var(--aipron-success) 8%, transparent)' }
                      : row.bg === 'accent' ? { background: 'var(--aipron-accent-muted)' }
                      : undefined
                    }
                  >
                    <td className={`py-2 px-2 font-black text-xs ${row.color}`}>{row.label}</td>
                    {adosaOverride.cycle_results.map((c: any, cIdx: number) => (
                      <td key={cIdx} className={`py-2 px-2 text-center text-xl font-black ${row.color}`}>
                        {c[row.key] || '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={`p-6 text-center ${textSub}`}>
            <p className="text-sm">{language === 'es' ? 'Pulse "Recalcular ADOSA" para obtener resultados' : 'Press "Recalculate ADOSA" to get results'}</p>
          </div>
        )}
      </div>

      {/* SAFETY INDICES + AVAILABILITY PIPELINE */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Safety Indices Breakdown */}
        <div className={`p-6 rounded-xl border ${cardBg}`}>
          <h3 className={`font-bold text-sm mb-4 ${textMain}`}>
            {language === 'es' ? 'Índices de Seguridad' : 'Safety Indices'}
          </h3>
          <div className="space-y-4">
            {[
              { label: language === 'es' ? 'Índice Absentismo' : 'Absenteeism Index', value: adosa.safety_index_abs, pct: Math.round(adosa.safety_index_abs * 100), color: 'var(--aipron-danger)' },
              { label: language === 'es' ? 'Índice Formación' : 'Training Index', value: adosa.safety_index_training, pct: Math.round(adosa.safety_index_training * 100), color: 'var(--aipron-accent)' },
              { label: language === 'es' ? 'Corrección Combinada' : 'Combined Correction', value: adosa.safety_correction_index, pct: Math.round(adosa.safety_correction_index * 100), color: 'var(--aipron-warning)' },
            ].map((idx_item, i) => (
              <div key={i}>
                <div className="flex justify-between text-xs mb-1">
                  <span className={textSub}>{idx_item.label}</span>
                  <span className={`font-bold ${textMain}`}>{(idx_item.value * 100).toFixed(1)}%</span>
                </div>
                <div className="h-2.5 rounded-full overflow-hidden bg-[var(--surface-inset)]">
                  <div className="h-full rounded-full transition-all duration-700" style={{ width: `${idx_item.pct}%`, background: idx_item.color }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Availability Pipeline */}
        <div className={`p-6 rounded-xl border ${cardBg}`}>
          <h3 className={`font-bold text-sm mb-4 ${textMain}`}>
            {language === 'es' ? 'Pipeline de Disponibilidad' : 'Availability Pipeline'}
          </h3>
          <div className="space-y-3">
            {[
              { label: language === 'es' ? 'Días del año' : 'Year days', value: 365, color: 'text-[var(--text-secondary)]' },
              { label: language === 'es' ? 'Días disponibles (ciclo)' : 'Available (cycle)', value: adosa.available_working_days, color: 'text-[var(--aipron-info)]' },
              { label: language === 'es' ? 'Días laborables (- vacaciones)' : 'Working (- holidays)', value: adosa.yearly_working_days, color: 'text-[var(--aipron-accent)]' },
              { label: language === 'es' ? '- Absentismo' : '- Absenteeism', value: adosa.absent_days, color: 'text-[var(--aipron-danger)]', subtract: true },
              { label: language === 'es' ? '- Formación' : '- Training', value: adosa.training_days, color: 'text-[var(--aipron-accent)]', subtract: true },
              { label: language === 'es' ? '= Días en servicio' : '= On-duty days', value: adosa.net_duty_days, color: 'text-[var(--aipron-success)]', highlight: true },
            ].map((step, i) => (
              <div
                key={i}
                className="flex justify-between items-center px-3 py-2 rounded-lg"
                style={(step as any).highlight ? { background: 'color-mix(in srgb, var(--aipron-success) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--aipron-success) 30%, transparent)' } : undefined}
              >
                <span className={`text-sm ${(step as any).subtract ? 'text-[var(--aipron-danger)] italic' : (step as any).highlight ? `font-bold ${step.color}` : textSub
                  }`}>{step.label}</span>
                <span className={`text-lg font-black ${step.color}`}>
                  {(step as any).subtract ? '-' : ''}{typeof step.value === 'number' ? step.value.toFixed(1) : step.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* SECTION 3: MONTHLY ROSTER GRID (CUADRANTE) — CLIENT-SIDE        */}
      {/* ════════════════════════════════════════════════════════════════ */}
      <div className={`p-6 rounded-xl border ${cardBg}`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className={`font-bold text-sm ${textMain}`}>
              {language === 'es' ? 'Cuadrante Mensual (30 días)' : 'Monthly Roster (30 days)'}
            </h3>
            <p className={`text-xs ${textSub}`}>
              {language === 'es'
                ? `T=Trabajo, L=Libre. Ciclo ${generateClientRoster.workDays}T/${generateClientRoster.offDays}L. Escalonamiento automático.`
                : `T=Work, L=Off. Cycle ${generateClientRoster.workDays}T/${generateClientRoster.offDays}L. Auto-staggered.`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div
              className="px-3 py-1.5 rounded-lg text-xs font-bold border"
              style={{ background: 'color-mix(in srgb, var(--aipron-info) 10%, transparent)', borderColor: 'color-mix(in srgb, var(--aipron-info) 30%, transparent)', color: 'var(--aipron-info)' }}
            >
              {language === 'es' ? 'Personas' : 'Staff'}: {generateClientRoster.headcount}
            </div>
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold border"
              style={generateClientRoster.monthly.coverage_ok
                ? { background: 'color-mix(in srgb, var(--aipron-success) 15%, transparent)', color: 'var(--aipron-success)', borderColor: 'color-mix(in srgb, var(--aipron-success) 30%, transparent)' }
                : { background: 'color-mix(in srgb, var(--aipron-danger) 15%, transparent)', color: 'var(--aipron-danger)', borderColor: 'color-mix(in srgb, var(--aipron-danger) 30%, transparent)' }
              }
            >
              {generateClientRoster.monthly.coverage_ok ? '✓' : '✗'}
              {generateClientRoster.monthly.coverage_ok
                ? (language === 'es' ? ' Cobertura ≥ DSR' : ' Coverage ≥ DSR')
                : (language === 'es' ? ' Déficit' : ' Deficit')}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr>
                <th className={`sticky left-0 z-10 py-1.5 px-2 text-left bg-[var(--surface)] ${textSub} font-semibold border-b border-[var(--border)]`}>
                  {language === 'es' ? 'Emp.' : 'Emp.'}
                </th>
                {Array.from({ length: 30 }, (_, i) => (
                  <th key={i} className={`py-1.5 px-1 text-center font-medium ${textSub} border-b ${'border-[var(--border)]'}`} style={{ minWidth: '28px' }}>
                    {i + 1}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {generateClientRoster.monthly.roster.map((empRow, empIdx) => (
                <tr key={empIdx} className={empIdx % 2 === 0 ? '' : 'bg-[var(--surface-inset)]'}>
                  <td className={`sticky left-0 z-10 py-1 px-2 font-bold text-xs bg-[var(--surface)] ${textMain} border-b border-[var(--border-subtle)]`}>
                    E{empIdx + 1}
                  </td>
                  {empRow.map((cell, dayIdx) => (
                    <td key={dayIdx} className={`py-1 px-0 text-center font-bold text-sm border-b border-[var(--border-subtle)] ${cell === 'T'
                      ? 'text-green-400 bg-green-500/10'
                      : 'text-[var(--text-tertiary)] bg-[var(--surface-inset)]'
                      }`}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
              {/* Coverage Σ row */}
              <tr style={{ background: 'color-mix(in srgb, var(--aipron-info) 8%, transparent)' }}>
                <td
                  className="sticky left-0 z-10 py-2 px-2 font-black text-xs text-[var(--aipron-info)] border-t-2"
                  style={{ background: 'color-mix(in srgb, var(--aipron-info) 18%, var(--surface))', borderTopColor: 'color-mix(in srgb, var(--aipron-info) 40%, transparent)' }}
                >
                  Σ
                </td>
                {generateClientRoster.monthly.daily_coverage.map((cov, dayIdx) => (
                  <td
                    key={dayIdx}
                    className="py-2 px-0 text-center font-black text-sm border-t-2"
                    style={{
                      borderTopColor: 'color-mix(in srgb, var(--aipron-info) 40%, transparent)',
                      color: cov >= generateClientRoster.dsr ? 'var(--aipron-success)' : 'var(--aipron-danger)',
                      background: cov >= generateClientRoster.dsr ? undefined : 'color-mix(in srgb, var(--aipron-danger) 10%, transparent)',
                    }}
                  >
                    {cov}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        <div className={`mt-3 flex items-center gap-4 text-xs ${textSub}`}>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500/20 border border-green-500/40" /> T = {language === 'es' ? 'Trabajo' : 'Work'}</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[var(--surface-inset)] border border-[var(--border-strong)]" /> L = {language === 'es' ? 'Libre' : 'Off'}</span>
          <span className="ml-auto font-bold">
            DSR = {generateClientRoster.dsr} | {language === 'es' ? 'Ciclo' : 'Cycle'}: {generateClientRoster.workDays}T/{generateClientRoster.offDays}L
          </span>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* SECTION 4: ANNUAL ROSTER SUMMARY + COVERAGE — CLIENT-SIDE       */}
      {/* ════════════════════════════════════════════════════════════════ */}
      <div className={`p-6 rounded-xl border ${cardBg}`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className={`font-bold text-sm ${textMain}`}>
              {language === 'es' ? 'Resumen Anual (365 días)' : 'Annual Summary (365 days)'}
            </h3>
            <p className={`text-xs ${textSub}`}>
              {language === 'es'
                ? 'V=Vacaciones, F=Formación. Inyección inteligente que nunca reduce la cobertura bajo DSR.'
                : 'V=Vacation, F=Training. Smart injection that never drops coverage below DSR.'}
            </p>
          </div>
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold border"
            style={generateClientRoster.annual.coverage_ok
              ? { background: 'color-mix(in srgb, var(--aipron-success) 15%, transparent)', color: 'var(--aipron-success)', borderColor: 'color-mix(in srgb, var(--aipron-success) 30%, transparent)' }
              : { background: 'color-mix(in srgb, var(--aipron-danger) 15%, transparent)', color: 'var(--aipron-danger)', borderColor: 'color-mix(in srgb, var(--aipron-danger) 30%, transparent)' }
            }
          >
            {generateClientRoster.annual.coverage_ok ? '✓' : '✗'}
            {generateClientRoster.annual.coverage_ok
              ? (language === 'es' ? ' 365 días ≥ DSR' : ' 365 days ≥ DSR')
              : (language === 'es' ? ' Días con déficit' : ' Days with deficit')}
          </div>
        </div>

        {/* Employee Stats Table */}
        <div className="overflow-x-auto mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className={`border-b ${'border-[var(--border)]'}`}>
                <th className={`text-left py-2 px-3 ${textSub} font-semibold`}>{language === 'es' ? 'Empleado' : 'Employee'}</th>
                <th className="text-center py-2 px-3 text-green-400 font-semibold">T</th>
                <th className="text-center py-2 px-3 text-[var(--text-secondary)] font-semibold">L</th>
                <th className="text-center py-2 px-3 text-blue-400 font-semibold">V</th>
                <th className="text-center py-2 px-3 text-purple-400 font-semibold">F</th>
                <th className={`text-center py-2 px-3 ${textSub} font-semibold`}>Total</th>
              </tr>
            </thead>
            <tbody>
              {generateClientRoster.annual.employee_stats.map((emp, idx) => (
                <tr key={idx} className={`border-b border-[var(--border-subtle)] ${idx % 2 === 0 ? '' : 'bg-[var(--surface-inset)]'}`}>
                  <td className={`py-2 px-3 font-bold ${textMain}`}>E{emp.employee}</td>
                  <td className="py-2 px-3 text-center text-green-400 font-bold">{emp.work_days}</td>
                  <td className="py-2 px-3 text-center text-[var(--text-secondary)]">{emp.off_days}</td>
                  <td className="py-2 px-3 text-center text-blue-400">{emp.vacation_days}</td>
                  <td className="py-2 px-3 text-center text-purple-400">{emp.training_days}</td>
                  <td className={`py-2 px-3 text-center font-bold ${textMain}`}>{emp.work_days + emp.off_days + emp.vacation_days + emp.training_days}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Monthly Coverage Summary */}
        <h4 className={`font-bold text-xs mb-3 ${textMain}`}>
          {language === 'es' ? 'Cobertura Mensual' : 'Monthly Coverage'}
        </h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {generateClientRoster.annual.months_summary.map((m, idx) => (
            <div
              key={idx}
              className="p-3 rounded-lg border text-center"
              style={
                m.days_below_dsr > 0
                  ? { background: 'color-mix(in srgb, var(--aipron-danger) 8%, transparent)', borderColor: 'color-mix(in srgb, var(--aipron-danger) 30%, transparent)' }
                  : { background: 'var(--surface-raised)', borderColor: 'var(--border)' }
              }
            >
              <div className={`text-xs font-bold ${textMain}`}>{m.month}</div>
              <div className="text-lg font-black" style={{ color: m.days_below_dsr > 0 ? 'var(--aipron-danger)' : 'var(--aipron-success)' }}>
                {m.avg_coverage}
              </div>
              <div className={`text-xs ${textSub}`}>
                min: {m.min_coverage} / max: {m.max_coverage}
              </div>
              {m.days_below_dsr > 0 && (
                <div className="text-xs font-bold mt-1" style={{ color: 'var(--aipron-danger)' }}>{m.days_below_dsr}d</div>
              )}
            </div>
          ))}
        </div>

        <div className={`mt-4 flex items-center gap-4 text-xs ${textSub}`}>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500/20 border border-green-500/40" /> T = {language === 'es' ? 'Trabajo' : 'Work'}</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[var(--surface-inset)] border border-[var(--border-strong)]" /> L = {language === 'es' ? 'Libre' : 'Off'}</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-500/20 border border-blue-500/40" /> V = {language === 'es' ? 'Vacaciones' : 'Vacation'}</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-purple-500/20 border border-purple-500/40" /> F = {language === 'es' ? 'Formación' : 'Training'}</span>
        </div>
      </div>

    </div>
  );
};

export default AdosaPage;
